import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DemoTourService, DemoTourStep } from 'src/shared/services/demo-tour.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

// How long the outgoing stop's content fades before the next stop's content
// slides in — roughly covering the page navigation happening underneath.
const CONTENT_SWAP_MS = 200;
// The nav is static, but it can still change under the tour (nav collapse, page
// switches, resizes) — re-measure the glow target on a slow cadence.
const GLOW_MEASURE_INTERVAL_MS = 400;

interface GlowRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * The guided demo tour "guide dock": a brand-teal lower-third panel fixed to the
 * bottom of the viewport, blended into the page by a soft gradient scrim. It does
 * NOT block the page — the app above stays fully visible and interactive, so the
 * dock reads as part of the page rather than a modal sitting on top of it.
 *
 * The page tie-in is a glow ring on the CURRENT PAGE'S NAV ITEM (left nav on
 * desktop, bottom nav on mobile): it highlights something real and stable — "this
 * is the page you're looking at, and here's where to find it again" — and, being
 * one persistent element, it glides from nav item to nav item as the tour
 * advances. Deliberately not arbitrary in-page elements (tried, felt random).
 *
 * The dock itself never moves: between stops only its content crossfades while
 * the page navigates behind it, and a slim progress line at the top fills as the
 * tour advances. DemoTourService owns the tour state (including following the
 * router when the user navigates on their own); this component renders the
 * current stop, choreographs the content swap, and positions the nav glow.
 */
@Component({
  selector: 'demo-tour-overlay',
  templateUrl: './demo-tour.component.html',
  styleUrls: ['./demo-tour.component.scss'],
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoTourComponent implements OnDestroy {

  readonly tour = inject(DemoTourService);

  // The step the dock currently SHOWS. Lags tour.currentStep() by the content's
  // leave animation, so the old copy fades out instead of swapping mid-flight.
  readonly displayStep = signal<DemoTourStep | null>(null);
  readonly phase = signal<'in' | 'out'>('in');
  // Glow ring over the current page's nav item; null while no target is visible.
  readonly navGlow = signal<GlowRect | null>(null);

  readonly displayIndex = computed(() => {
    const step = this.displayStep();
    const index = step ? this.tour.steps().findIndex((s) => s.id === step.id) : -1;
    return index < 0 ? 0 : index;
  });

  readonly progressPct = computed(() => {
    const count = this.tour.steps().length || 1;
    return ((this.displayIndex() + 1) / count) * 100;
  });

  readonly nextLabel = computed(() => {
    const steps = this.tour.steps();
    const index = this.displayIndex();
    return index >= steps.length - 1 ? 'Start Exploring' : `Next: ${steps[index + 1].name}`;
  });

  private swapTimer: any = null;
  private glowTimer: any = null;
  // One attempt per stop at opening a collapsed nav section (navSectionToggle),
  // so the tour never fights a user who collapses it again.
  private sectionToggleAttempted = false;

  constructor() {
    effect(() => {
      const active = this.tour.active();
      const step = this.tour.currentStep();
      if (!active || !step) {
        this.stopTimers();
        this.displayStep.set(null);
        this.phase.set('in');
        this.navGlow.set(null);
        return;
      }
      this.startGlowMeasuring();
      const shown = this.displayStep();
      if (!shown) {
        this.showStep(step);
        return;
      }
      if (shown.id === step.id) {
        return;
      }
      clearTimeout(this.swapTimer);
      this.phase.set('out');
      this.swapTimer = setTimeout(() => this.showStep(step), CONTENT_SWAP_MS);
    });
  }

  private showStep(step: DemoTourStep): void {
    this.displayStep.set(step);
    this.phase.set('in');
    this.sectionToggleAttempted = false;
    // The nav is persistent across pages, so the ring can glide to the new
    // page's item immediately — it doesn't wait for the page to render.
    this.measureGlow();
  }

  ngOnDestroy(): void {
    this.stopTimers();
  }

  next = () => {
    this.tour.next();
  };

  skip = () => {
    this.tour.skip();
  };

  private startGlowMeasuring(): void {
    if (!this.glowTimer) {
      this.glowTimer = setInterval(() => this.measureGlow(), GLOW_MEASURE_INTERVAL_MS);
    }
  }

  private stopTimers(): void {
    clearTimeout(this.swapTimer);
    this.swapTimer = null;
    if (this.glowTimer) {
      clearInterval(this.glowTimer);
      this.glowTimer = null;
    }
  }

  private measureGlow(): void {
    const step = this.displayStep();
    if (!step) {
      this.navGlow.set(null);
      return;
    }
    // The stop's real nav entry can live inside a collapsed section (Optimizer
    // under Revenue): open the section once so the ring lands on the entry
    // itself. Only when the entry is absent — clicking an open section would
    // collapse it.
    if (step.navSectionToggle && !this.sectionToggleAttempted
      && !document.querySelector(step.navTargets[0])) {
      this.sectionToggleAttempted = true;
      document.querySelector<HTMLElement>(step.navSectionToggle)?.click();
    }
    for (const selector of step.navTargets) {
      const el = document.querySelector<HTMLElement>(selector);
      if (!el) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        continue;
      }
      const pad = 4;
      const next: GlowRect = {
        top: Math.round(rect.top - pad),
        left: Math.round(rect.left - pad),
        width: Math.round(rect.width + pad * 2),
        height: Math.round(rect.height + pad * 2),
      };
      const prev = this.navGlow();
      if (!prev || prev.top !== next.top || prev.left !== next.left || prev.width !== next.width || prev.height !== next.height) {
        this.navGlow.set(next);
      }
      return;
    }
    this.navGlow.set(null);
  }
}
